import Menu from '@/global/components/MenuV2'
import { Tooltip } from '@/global/components/ui/tooltip'
import { useTransmit } from '@/hooks/useTransmit'
import {
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuList,
    Paper,
    Popper,
} from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { ArrowDropDownIcon } from '@mui/x-date-pickers'
import { Button } from 'global/components/Button'
import { CalculatorIcon, ShieldBanIcon } from 'lucide-react'
import React from 'react'
import { calculo, espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { useCalculoStore } from './store'
import { EventCalculoMessage, EventEspelhoPdfMessage } from './types'
import { downloadPdf } from './utils'

type Props = {
    competencia: string
}

const options = [
    {
        btnLabel: 'Calcular o ponto',
        menuLabel: 'Calcular somente o ponto',
        value: 'CALCULAR',
    },
    {
        btnLabel: 'Calcular e baixar PDF',
        menuLabel: 'Calcular e baixar relatório PDF',
        value: 'PDF',
    },
    {
        btnLabel: 'Calcular e baixar ZIP',
        menuLabel: 'Calcular e baixar arquivo ZIP',
        value: 'ZIP',
    },
] as const

const CalcularEmpresa: React.FC<Props> = ({ competencia }) => {
    const { progresso, state } = useCalculoStore(store => store.state)
    const dispatch = useCalculoStore(store => store.dispatch)

    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const competenciaAberta = useStore(state => state.company?.competenciaAtual)

    const [open, setOpen] = React.useState(false)
    const anchorRef = React.useRef<HTMLDivElement>(null)
    const [selectedIndex, setSelectedIndex] = React.useState(0)

    const handleClick = async () => {
        const option = options[selectedIndex].value

        await mutateAsync({
            competencia,
            empresaId,
            centroCustoId: centroCustoId > 0 ? centroCustoId : undefined,
            baixarPdf: option === 'PDF',
            baixarZip: option === 'ZIP',
        })
    }

    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number,
    ) => {
        setSelectedIndex(index)
        setOpen(false)
    }

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen)
    }

    const handleClose = (event: Event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return
        }

        setOpen(false)
    }

    const subscribePdfProcessor = useTransmit<EventEspelhoPdfMessage>({
        onMessage: async ({ status, data }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    if (useCalculoStore.getState().state.progresso === null) {
                        toast.info(
                            'Estamos gerando os espelhos de ponto. Fique à vontade para utilizar o sistema normalmente.',
                            {
                                duration: 5 * 1000,
                            },
                        )
                    }

                    dispatch({ action: 'PROGRESSO', data: data.progresso })

                    break
                case 'ERRO':
                    toast.error(data.message)
                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'GERANDO_PDF', data: false },
                    )
                    break
                case 'FINALIZADO':
                    if (data.pdf) downloadPdf(data.pdf)
                    if (data.zip) {
                        await espelhoPonto.calculos.file(`zip/${data.zip}`)
                    }

                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'GERANDO_PDF', data: false },
                    )
                    toast.success('Arquivos gerados com sucesso')
                    break
            }
        },
    })

    const subscribeCalculoProcessor = useTransmit<EventCalculoMessage>({
        onMessage: async ({ status, progresso, message }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    dispatch({ action: 'PROGRESSO', data: progresso })
                    break
                case 'ERRO':
                    dispatch({ action: 'PROGRESSO', data: null })
                    await unsubscribe()
                    espelhoPonto.invalidateQueries()
                    toast.error(message ?? 'Ocorreu um erro no cálculo')
                    break
                default:
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        {
                            action: 'GERANDO_PDF',
                            data: true,
                        },
                    )

                    await unsubscribe()
                    espelhoPonto.invalidateQueries()
                    toast.success('O cálculo foi realizado com sucesso')

                    const channel = `processor:${
                        useCalculoStore.getState().state.ultimoProcesso
                    }`

                    await subscribePdfProcessor(channel)
                    break
            }
        },
    })

    const { mutateAsync, isLoading } = calculo.historico.mutations.useCreate({
        onMutate: () => dispatch({ action: 'PROGRESSO', data: 0 }),
        onSuccess: async data => {
            dispatch({ action: 'ULTIMO_PROCESSO', data: data.id })
            await subscribeCalculoProcessor(data.cod)
        },
        onError: async err => {
            dispatch({ action: 'PROGRESSO', data: null })
            toast.error(err.message)
        },
    })

    const disableButton = React.useMemo(
        () =>
            isLoading ||
            competenciaAberta !== competencia ||
            progresso != null ||
            state !== 'idle',
        [isLoading, competenciaAberta, competencia, progresso, state],
    )

    return (
        <div className="flex flex-1 items-center justify-end gap-3">
            <Tooltip
                title={
                    competenciaAberta !== competencia
                        ? 'Competência fechada'
                        : undefined
                }
            >
                <div>
                    <ButtonGroup
                        variant="contained"
                        ref={anchorRef}
                        aria-label="Button group with a nested menu"
                        className="rounded-sm overflow-hidden [&_button]:border-0! relative h-10 w-74"
                        disabled={disableButton}
                    >
                        <Button
                            onClick={handleClick}
                            className="w-full truncate text-[length:inherit] justify-around"
                            startIcon={
                                competenciaAberta !== competencia ? (
                                    <ShieldBanIcon />
                                ) : (
                                    <CalculatorIcon />
                                )
                            }
                            id="calcular-empresa"
                        >
                            {(() => {
                                if (competenciaAberta !== competencia)
                                    return 'Competência fechada'
                                if (state === 'baixando')
                                    return 'Baixando arquivos...'
                                if (state === 'gerando')
                                    return 'Gerando PDFs...'
                                if (isLoading || disableButton)
                                    return 'Calculando...'
                                return options[selectedIndex].btnLabel
                            })()}
                        </Button>
                        <div className="h-full border w-[1px]" />
                        <Button
                            size="small"
                            aria-controls={
                                open ? 'split-button-menu' : undefined
                            }
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            onClick={handleToggle}
                            sx={{
                                width: 24,
                            }}
                            className="border-r-0"
                        >
                            <ArrowDropDownIcon />
                        </Button>
                        <LinearProgress
                            color="inherit"
                            variant="determinate"
                            value={progresso ?? 0}
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                borderRadius: '0 0 4px 4px',
                                left: 0,
                                right: 0,
                                visibility:
                                    progresso != null ? 'visible' : 'hidden',
                            }}
                        />
                    </ButtonGroup>
                    <Popper
                        sx={{ zIndex: 99999999 }}
                        open={open}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        transition
                        disablePortal
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === 'bottom'
                                            ? 'left top'
                                            : 'left bottom',
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener
                                        onClickAway={handleClose}
                                    >
                                        <MenuList
                                            id="split-button-menu"
                                            autoFocusItem
                                        >
                                            {options.map((option, index) => (
                                                <Menu.Item
                                                    key={option.value}
                                                    selected={
                                                        index === selectedIndex
                                                    }
                                                    onClick={event =>
                                                        handleMenuItemClick(
                                                            event,
                                                            index,
                                                        )
                                                    }
                                                >
                                                    {option.menuLabel}
                                                </Menu.Item>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </div>
            </Tooltip>
        </div>
    )
}

export default CalcularEmpresa
